import { Spin } from 'antd'
import { useFormik } from 'formik'
import { useContext, useState } from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Form } from 'sutra/components/Form'
import { accents } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import * as Yup from 'yup'

import ArrowLeft from 'assets/images/Arrow-Left.svg'
import { logger } from 'clients/logger'
import { AuthContext } from 'features/auth'
import { AuthCard } from 'features/auth/components/AuthCard/AuthCard'
import { AuthLayout } from 'features/auth/components/AuthLayout'
import { FormItem } from 'features/auth/components/FormItem'
import { FormSent } from 'features/auth/components/FormSent'
import { Input } from 'features/auth/components/Input'
import { ServerValidationError } from 'features/auth/components/ServerValidationError'
import { SIGN_IN } from 'features/auth/paths'
import { useAuthService } from 'features/auth/service'
import { resetPasswordSendCode } from 'features/auth/utils'

const ResetPasswordSchema = Yup.object().shape({
  username: Yup.string().email('Invalid email').required('Required'),
})

// Amplify reports the discriminating detail on `name`, not the message.
type AuthError = { name?: string; message?: string }

// Cognito refuses its native forgot-password flow with NotAuthorizedException
// for a user who was invited but never set a password (FORCE_CHANGE_PASSWORD).
// That refusal hands off to the backend's activation resend (the same route
// /setup-password recovers with), which checks the account state itself and
// only re-sends the invitation for an account still awaiting activation. The
// same exception name also covers refusals with no recovery (a disabled or
// SSO user).
const isRefusedByIdentityProvider = (err: AuthError): boolean =>
  err.name === 'NotAuthorizedException'

// This page is public, so its outcome must not reveal whether an email has an
// account or what state that account is in. Every per-account outcome — reset
// link sent, activation email sent, nothing sent because the account is
// unknown, disabled, or SSO — shows the same "sent" screen with the same
// wording. Only a failure of our own request (not a Cognito verdict about the
// account) surfaces an error, and that error carries no account detail.
const SENT_NOUN = 'an email with next steps'
const GENERIC_ERROR = 'Something went wrong. Please try again.'

export const ResetPasswordForm = (): JSX.Element => {
  const [isFormSent, setIsFormSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [serverValidationError, setServerValidationError] = useState('')
  const authContext = useContext(AuthContext)
  // bully ignore[no-service-hooks-outside-containers: route-level auth form owns its own wiring (no Container/View split in features/auth/forms), same shape as the sibling SetupPasswordForm; refactor: when the auth forms migrate to the Container/View pattern]
  const authService = useAuthService()
  const formik = useFormik({
    initialValues: { username: '' },
    validationSchema: ResetPasswordSchema,
    onSubmit: async ({ username }) => {
      const normalized_username = username.toLowerCase()
      setLoading(true)
      try {
        await resetPasswordSendCode(normalized_username)
        authContext.updateUsername(normalized_username)
        setIsFormSent(true)
      } catch (e) {
        const err = e as AuthError
        if (isRefusedByIdentityProvider(err)) {
          try {
            await authService.resendAccountActivation(normalized_username)
            setIsFormSent(true)
          } catch (resendError) {
            logger.error('reset-password: activation resend failed', {
              error: resendError,
            })
            setServerValidationError(GENERIC_ERROR)
          }
        } else if (err.name === 'UserNotFoundException') {
          // Same screen as success: an unknown email must look identical to a
          // known one from the outside.
          setIsFormSent(true)
        } else {
          logger.error('reset-password: native reset failed', { error: err })
          setServerValidationError(GENERIC_ERROR)
        }
      } finally {
        setLoading(false)
      }
    },
  })

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setServerValidationError('')
    formik.handleChange(e)
  }

  const usernameError = formik.touched.username && formik.errors.username

  const renderContent = (): JSX.Element => {
    // State 3: Email sent
    if (isFormSent) {
      return (
        <AuthCard>
          <StyledForm layout="vertical">
            <FormSent email={formik.values.username} linkNoun={SENT_NOUN} />
            <BackLinkContainer>
              <StyledLink to={SIGN_IN}>
                <Back>
                  <StyledArrowLeft src={ArrowLeft} />
                  Back to Login Page
                </Back>
              </StyledLink>
            </BackLinkContainer>
          </StyledForm>
        </AuthCard>
      )
    }

    // State 2: Email sending
    if (loading) {
      return (
        <AuthCard>
          <StyledForm layout="vertical">
            <SendingContainer>
              <Spin size="large" />
              <SendingText>Sending reset link...</SendingText>
            </SendingContainer>
            <BackLinkContainer>
              <StyledLink to={SIGN_IN}>
                <Back>
                  <StyledArrowLeft src={ArrowLeft} />
                  Back to Login Page
                </Back>
              </StyledLink>
            </BackLinkContainer>
          </StyledForm>
        </AuthCard>
      )
    }

    // State 1: Reset form (default)
    return (
      <AuthCard>
        <StyledForm onFinish={formik.submitForm} layout="vertical">
          <Description>Enter your email to receive a reset link</Description>
          <StyledFormItem
            label="Email"
            validateStatus={usernameError ? 'error' : undefined}
            help={usernameError}
          >
            <Input
              placeholder="john.smith@acmelabs.com"
              name="username"
              error={!!usernameError}
              autoComplete="username"
              onChange={onChange}
              value={formik.values.username}
            />
          </StyledFormItem>
          {serverValidationError && (
            <ServerValidationError message={serverValidationError} />
          )}
          <SubmitButton type="submit">Submit</SubmitButton>
          <BackLinkContainer>
            <StyledLink to={SIGN_IN}>
              <Back>
                <StyledArrowLeft src={ArrowLeft} />
                Back to Login Page
              </Back>
            </StyledLink>
          </BackLinkContainer>
        </StyledForm>
      </AuthCard>
    )
  }

  return <AuthLayout>{renderContent()}</AuthLayout>
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const SubmitButton = styled(SutraButton)`
  width: 100%;
  height: 48px;
  font-weight: 600;
  border-radius: ${radii.sm}px;
`

const Description = styled.div`
  margin-bottom: 10px;
  margin-top: 10px;
  text-align: left;
`

const SendingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
`

const SendingText = styled.div`
  color: var(--color-auth-grey);
  font-size: 16px;
`

const BackLinkContainer = styled.div`
  padding: 16px 20px;
`

const Back = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`

const StyledArrowLeft = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 10px;
`

const StyledLink = styled(Link)`
  font-size: var(--font-size-md);
  justify-content: flex-start;
  color: ${accents.reflectionBlue[500]};

  :hover {
    text-decoration: underline;
  }
`

const StyledFormItem = styled(FormItem)`
  margin: 0;
  width: 100%;
`
